import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useCrudItem } from "../../../hooks/useCrud"
import type {
  AgencyUser,
  AgencyUserFormValues,
  CopyableIntegration,
  Integration,
} from "../models/agencyUser"
import { AgencyUserSchema } from "../models/agencyUser"
import { agencyUserApi } from "../services/agencyUserApi"

const BASE_KEY = "agency-users"
const BASE_URL = "agency-user"
const INT_KEY = "agency-user-integrations"

export function useAgencyUser(id?: number | null) {
  return useCrudItem<AgencyUser>(BASE_KEY, BASE_URL, AgencyUserSchema, id)
}

export function useCreateAgencyUser() {
  const queryClient = useQueryClient()
  return useMutation<AgencyUser, Error, AgencyUserFormValues>({
    mutationFn: (values) => agencyUserApi.create(values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_KEY] })
    },
  })
}

export function useUpdateAgencyUser() {
  const queryClient = useQueryClient()
  return useMutation<
    AgencyUser,
    Error,
    { id: number; values: Partial<AgencyUserFormValues> }
  >({
    mutationFn: ({ id, values }) => agencyUserApi.update(id, values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_KEY] })
    },
  })
}

export function useDeleteAgencyUser() {
  const queryClient = useQueryClient()
  return useMutation<void, Error, number>({
    mutationFn: (id) => agencyUserApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_KEY] })
    },
  })
}

export function useActivateAgencyUser() {
  const queryClient = useQueryClient()
  return useMutation<AgencyUser, Error, number>({
    mutationFn: (id) => agencyUserApi.activate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_KEY] })
    },
  })
}

export function useDeactivateAgencyUser() {
  const queryClient = useQueryClient()
  return useMutation<AgencyUser, Error, number>({
    mutationFn: (id) => agencyUserApi.deactivate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_KEY] })
    },
  })
}

export function useResetPassword() {
  const queryClient = useQueryClient()
  return useMutation<void, Error, { id: number; password: string }>({
    mutationFn: ({ id, password }) => agencyUserApi.resetPassword(id, password),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_KEY] })
    },
  })
}

export function useIntegrations(userId: number) {
  return useQuery<Integration[]>({
    queryKey: [INT_KEY, userId],
    queryFn: async () => {
      const result = await agencyUserApi.getIntegrations(userId)
      return (result?.items ?? result) as Integration[]
    },
    staleTime: 1000 * 60 * 2,
  })
}

export function useDeleteIntegration() {
  const queryClient = useQueryClient()
  return useMutation<void, Error, { id: number; userId: number }>({
    mutationFn: ({ id }) => agencyUserApi.deleteIntegration(id),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [INT_KEY, variables.userId] })
    },
  })
}

export function useCopyableIntegrations(userId: number) {
  return useQuery<CopyableIntegration[]>({
    queryKey: [BASE_KEY, "copyable-integrations", userId],
    queryFn: () => agencyUserApi.getCopyableIntegrations(userId),
    staleTime: 1000 * 60 * 2,
  })
}

export function useCopyIntegrations() {
  const queryClient = useQueryClient()
  return useMutation<void, Error, { userId: number; selection: number[] }>({
    mutationFn: ({ userId, selection }) =>
      agencyUserApi.copyIntegrations(userId, selection),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [INT_KEY, variables.userId] })
      queryClient.invalidateQueries({
        queryKey: [BASE_KEY, "copyable-integrations", variables.userId],
      })
    },
  })
}

export function useChangeAvatar() {
  const queryClient = useQueryClient()
  return useMutation<AgencyUser, Error, { userId: number; file: File }>({
    mutationFn: ({ userId, file }) => agencyUserApi.changeAvatar(userId, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_KEY] })
      queryClient.invalidateQueries({ queryKey: ["user"] })
      queryClient.invalidateQueries({ queryKey: ["dashboard"] })
    },
  })
}
